import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";
import DotTrendChart from "examples/Charts/DotTrendChart";
import dayjs from "dayjs";
import { memo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

const { black, dark, primary, success, error } = colors;
const { borderWidth } = borders;

function SatisfactionTrendChart({ item }) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const trend = Array.isArray(item?.trend) ? item.trend : [];
  const labels = Array.isArray(item?.labels)
    ? item.labels.map((label) => (dayjs(label).isValid() ? dayjs(label).format("MM/DD") : label))
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];

  const firstValue = Number(trend?.[0] || 0);
  const lastValue = Number(trend?.[trend.length - 1] || 0);
  const change = lastValue - firstValue;
  const isPositive = change >= 0;

  const tickAmount = isMobile ? 6 : isTablet ? 15 : labels.length;
  const chartData = [
    {
      name: item?.question || "Rating",
      data: trend,
    },
  ];

  const chartOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      background: "transparent",
      fontFamily: "inherit",
      animations: {
        enabled: false,
      },
    },

    stroke: {
      show: true,
      width: 3,
      curve: "smooth",
      // curve: "stepline",
      colors: [primary.main],
    },

    markers: {
      size: 3,
      strokeWidth: 1,
      strokeColors: "#e6ded1",
      colors: [primary.main],
      hover: {
        size: 4,
      },
    },

    colors: [primary.main],

    grid: {
      show: true,
      borderColor: rgba(black.main, 0.1),
      strokeDashArray: 4,
      padding: {
        top: 28,
        right: 24,
        bottom: 0,
        left: 30,
      },
    },

    xaxis: {
      type: "category",
      categories: labels,
      tickAmount,
      tickPlacement: "on",
      labels: {
        hideOverlappingLabels: true,
        rotate: -45,
        rotateAlways: true,
        offsetY: 2,
        style: {
          colors: labels.map(() => rgba(dark.main, 0.6)),
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },

    yaxis: {
      min: 1,
      max: 5,
      tickAmount: 4,
      labels: {
        offsetX: -12,
        formatter: (value) => Number(value).toFixed(0),
        style: {
          colors: labels.map(() => rgba(dark.main, 0.6)),
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },

    tooltip: {
      theme: "light",
      marker: {
        show: true,
      },
      y: {
        formatter: (value) => `${Number(value).toFixed(1)} / 5`,
      },
    },

    dataLabels: {
      enabled: true,
      formatter: (value) => Number(value).toFixed(1),
      offsetY: -14,
      style: {
        fontSize: "12px",
        fontWeight: 700,
        colors: [dark.main],
      },
      background: {
        enabled: false,
      },
    },

    legend: {
      show: false,
    },
  };

  return (
    <VuiBox
      p={1.5}
      borderRadius="16px"
      sx={{
        background: rgba(black.main, 0.025),
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
      }}
    >
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <VuiBox>
          <VuiTypography variant="button" color="dark" fontWeight="bold">
            {item?.question || "Satisfaction Trend"}
          </VuiTypography>

          <VuiTypography
            display="block"
            variant="caption"
            color="dark"
            fontWeight="medium"
            sx={{ opacity: 0.65 }}
          >
            Average rating over selected period
          </VuiTypography>
        </VuiBox>

        <VuiBox
          display="flex"
          alignItems="baseline"
          justifyContent="flex-end"
          gap={0.5}
          minWidth="70px"
        >
          <VuiTypography variant="h5" fontWeight="bold">
            {Number(item?.average || lastValue || 0).toFixed(1)}
          </VuiTypography>

          <VuiTypography variant="caption" fontWeight="bold">
            / 5
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <VuiBox height="200px">
        {trend.length ? (
          <DotTrendChart chartData={chartData} chartOptions={chartOptions} height="100%" />
        ) : (
          <VuiBox
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <VuiTypography variant="caption" color="dark" fontWeight="medium">
              No trend data available.
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>

      <VuiBox
        mt={1}
        pt={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderTop: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
        }}
      >
        <VuiTypography variant="caption" color="dark" fontWeight="medium">
          {item?.responses || 0} responses
        </VuiTypography>

        {/* <VuiTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            color: isPositive ? success.main : error.main,
          }}
        >
          {isPositive ? "+" : ""}
          {change.toFixed(1)} rating change
        </VuiTypography> */}
      </VuiBox>
    </VuiBox>
  );
}

export default memo(SatisfactionTrendChart);
